import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { homeLabsAPI, adminAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { 
  TestTube, 
  Package, 
  Plus,
  MagnifyingGlass,
  User,
  Clock,
  CheckCircle,
  SpinnerGap,
  PaperPlaneTilt,
  Heart,
  Lightning,
  Flame,
  Drop,
  FirstAidKit,
  ArrowsClockwise,
  Upload
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const statusColors = {
  pending: 'bg-yellow-100 text-yellow-800',
  ordered: 'bg-blue-100 text-blue-800',
  kit_shipped: 'bg-purple-100 text-purple-800',
  kit_delivered: 'bg-indigo-100 text-indigo-800',
  sample_collected: 'bg-cyan-100 text-cyan-800',
  sample_received: 'bg-teal-100 text-teal-800',
  processing: 'bg-orange-100 text-orange-800',
  resulted: 'bg-green-100 text-green-800',
  cancelled: 'bg-red-100 text-red-800'
};

const statusLabels = {
  pending: 'Pending',
  ordered: 'Ordered',
  kit_shipped: 'Kit Shipped',
  kit_delivered: 'Kit Delivered',
  sample_collected: 'Sample Collected',
  sample_received: 'Lab Received',
  processing: 'Processing',
  resulted: 'Results Ready',
  cancelled: 'Cancelled'
};

const panelIcons = {
  'siphox-comprehensive': <FirstAidKit size={24} className="text-blue-600" />,
  'siphox-metabolic': <Lightning size={24} className="text-amber-600" />,
  'siphox-thyroid': <Flame size={24} className="text-purple-600" />,
  'siphox-cardiovascular': <Heart size={24} className="text-red-600" />,
  'siphox-hormone-male': <User size={24} className="text-indigo-600" />,
  'siphox-hormone-female': <User size={24} className="text-pink-600" />,
  'rythm-monthly': <ArrowsClockwise size={24} className="text-teal-600" />
};

export default function StaffHomeLabsPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [panels, setPanels] = useState([]);
  const [patients, setPatients] = useState([]);
  const [config, setConfig] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Order dialog state
  const [showOrderDialog, setShowOrderDialog] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [selectedPanel, setSelectedPanel] = useState(null);
  const [orderNotes, setOrderNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  
  // Results upload dialog
  const [showResultsDialog, setShowResultsDialog] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [resultsJson, setResultsJson] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [ordersRes, panelsRes, configRes] = await Promise.all([
        homeLabsAPI.getOrders(),
        homeLabsAPI.getPanels(),
        homeLabsAPI.getConfig()
      ]);
      setOrders(ordersRes.data.orders || []);
      setPanels(panelsRes.data.panels || []);
      setConfig(configRes.data);
      
      // Load patients separately to avoid blocking if it fails
      try {
        const patientsRes = await adminAPI.getUsers();
        // Filter to only patients
        setPatients((patientsRes.data || []).filter(u => u.role === 'patient'));
      } catch (patientError) {
        console.warn('Could not load patients:', patientError);
        setPatients([]);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOrder = async () => {
    if (!selectedPatient || !selectedPanel) {
      toast.error('Please select a patient and panel');
      return;
    }
    
    try {
      setSubmitting(true);
      const response = await homeLabsAPI.createOrder({
        patient_id: selectedPatient.id,
        panel_id: selectedPanel.id,
        provider: selectedPanel.provider,
        notes: orderNotes
      });
      
      toast.success('Lab order created successfully');
      setShowOrderDialog(false);
      setSelectedPatient(null);
      setSelectedPanel(null);
      setOrderNotes('');
      loadData();
      
      // If SiPhox is configured, offer to send immediately
      if (selectedPanel.provider === 'siphox' && config?.providers?.siphox?.configured) {
        if (window.confirm('Send order to SiPhox now? The kit will be shipped to the patient.')) {
          await homeLabsAPI.sendToSiphox(response.data.order.id);
          toast.success('Order sent to SiPhox - kit will ship soon!');
          loadData();
        }
      }
    } catch (error) {
      toast.error('Failed to create order');
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSendToSiphox = async (orderId) => {
    try {
      await homeLabsAPI.sendToSiphox(orderId);
      toast.success('Order sent to SiPhox!');
      loadData();
    } catch (error) {
      toast.error('Failed to send to SiPhox');
      console.error(error);
    }
  };

  const handleUploadResults = async () => {
    if (!selectedOrder || !resultsJson) {
      toast.error('Please enter results');
      return;
    }
    
    try {
      setSubmitting(true);
      let parsedResults;
      try {
        parsedResults = JSON.parse(resultsJson);
      } catch (e) {
        toast.error('Invalid JSON format');
        return;
      }
      
      await homeLabsAPI.uploadResults(selectedOrder.id, {
        order_id: selectedOrder.id,
        results: parsedResults
      });
      
      toast.success('Results uploaded successfully');
      setShowResultsDialog(false);
      setSelectedOrder(null);
      setResultsJson('');
      loadData();
    } catch (error) {
      toast.error('Failed to upload results');
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  const filteredOrders = orders.filter(order => {
    const matchesSearch = 
      order.patient_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      order.panel?.name?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || order.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <SpinnerGap size={32} className="animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-teal-100 rounded-lg">
            <TestTube size={28} weight="duotone" className="text-teal-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
              At-Home Lab Orders
            </h1>
            <p className="text-slate-500 text-sm">Order and manage at-home lab tests for patients</p>
          </div>
        </div>
        <Button 
          onClick={() => setShowOrderDialog(true)}
          className="bg-teal-600 hover:bg-teal-700"
        >
          <Plus size={18} className="mr-2" />
          New Lab Order
        </Button>
      </div>

      {/* Provider Status */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <Card className={config?.providers?.siphox?.configured ? 'border-green-200' : 'border-yellow-200'}>
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <TestTube size={24} className="text-blue-600" />
                <div>
                  <h3 className="font-semibold">SiPhox Health</h3>
                  <p className="text-xs text-slate-500">7-10 day turnaround</p>
                </div>
              </div>
              <Badge className={config?.providers?.siphox?.configured ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>
                {config?.providers?.siphox?.configured ? 'API Connected' : 'Manual Entry'}
              </Badge>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-slate-200">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Drop size={24} className="text-teal-600" />
                <div>
                  <h3 className="font-semibold">Rythm Health</h3>
                  <p className="text-xs text-slate-500">48-hour turnaround</p>
                </div>
              </div>
              <Badge className="bg-yellow-100 text-yellow-800">
                Manual Entry
              </Badge>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex gap-4 mb-6">
        <div className="flex-1 relative">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Search by patient or panel..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2 border rounded-lg bg-white"
        >
          <option value="all">All Status</option>
          <option value="pending">Pending</option>
          <option value="ordered">Ordered</option>
          <option value="kit_shipped">Kit Shipped</option>
          <option value="processing">Processing</option>
          <option value="resulted">Results Ready</option>
        </select>
      </div>

      {/* Orders Table */}
      <Card>
        <CardContent className="p-0">
          <table className="w-full">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="text-left p-4 font-medium text-slate-600">Patient</th>
                <th className="text-left p-4 font-medium text-slate-600">Panel</th>
                <th className="text-left p-4 font-medium text-slate-600">Provider</th>
                <th className="text-left p-4 font-medium text-slate-600">Status</th>
                <th className="text-left p-4 font-medium text-slate-600">Ordered</th>
                <th className="text-right p-4 font-medium text-slate-600">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredOrders.length === 0 ? (
                <tr>
                  <td colSpan={6} className="p-8 text-center text-slate-500">
                    No orders found
                  </td>
                </tr>
              ) : (
                filteredOrders.map(order => (
                  <tr key={order.id} className="border-b hover:bg-slate-50">
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <User size={18} className="text-slate-400" />
                        <span className="font-medium">{order.patient_name}</span>
                      </div>
                    </td>
                    <td className="p-4">
                      <span className="text-sm">{order.panel?.name}</span>
                    </td>
                    <td className="p-4">
                      <span className="text-sm capitalize">{order.provider}</span>
                    </td>
                    <td className="p-4">
                      <Badge className={statusColors[order.status]}>
                        {statusLabels[order.status]}
                      </Badge>
                    </td>
                    <td className="p-4 text-sm text-slate-500">
                      {new Date(order.created_at).toLocaleDateString()}
                    </td>
                    <td className="p-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {order.status === 'pending' && order.provider === 'siphox' && config?.providers?.siphox?.configured && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleSendToSiphox(order.id)}
                          >
                            <PaperPlaneTilt size={16} className="mr-1" />
                            Send to SiPhox
                          </Button>
                        )}
                        {order.status !== 'resulted' && order.status !== 'cancelled' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setSelectedOrder(order);
                              setShowResultsDialog(true);
                            }}
                          >
                            <Upload size={16} className="mr-1" />
                            Upload Results
                          </Button>
                        )}
                        {order.status === 'resulted' && (
                          <Button size="sm" variant="ghost" className="text-green-600">
                            <CheckCircle size={16} className="mr-1" />
                            View Results
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* New Order Dialog */}
      <Dialog open={showOrderDialog} onOpenChange={setShowOrderDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Order At-Home Lab Test</DialogTitle>
            <DialogDescription>
              Select a patient and lab panel to order
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Patient Selection */}
            <div>
              <Label className="mb-2 block">Select Patient</Label>
              <select
                value={selectedPatient?.id || ''}
                onChange={(e) => {
                  const patient = patients.find(p => p.id === e.target.value);
                  setSelectedPatient(patient);
                }}
                className="w-full px-4 py-2 border rounded-lg"
              >
                <option value="">Choose a patient...</option>
                {patients.map(patient => (
                  <option key={patient.id} value={patient.id}>
                    {patient.first_name} {patient.last_name} - {patient.email}
                  </option>
                ))}
              </select>
            </div>

            {/* Panel Selection */}
            <div>
              <Label className="mb-2 block">Select Lab Panel</Label>
              <div className="grid grid-cols-1 gap-3 max-h-64 overflow-y-auto">
                {panels.map(panel => (
                  <div
                    key={panel.id}
                    onClick={() => setSelectedPanel(panel)}
                    className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                      selectedPanel?.id === panel.id 
                        ? 'border-teal-500 bg-teal-50' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      {panelIcons[panel.id] || <TestTube size={24} className="text-slate-400" />}
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <h4 className="font-semibold">{panel.name}</h4>
                          <span className="text-sm font-medium text-slate-600">${panel.price}</span>
                        </div>
                        <p className="text-sm text-slate-500 mt-1">{panel.description}</p>
                        <div className="flex items-center gap-3 mt-2 text-xs text-slate-400">
                          <span>{panel.turnaround_days} days</span>
                          <span className="capitalize">{panel.provider}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Notes */}
            <div>
              <Label className="mb-2 block">Clinical Notes (Optional)</Label>
              <Textarea
                value={orderNotes}
                onChange={(e) => setOrderNotes(e.target.value)}
                placeholder="Any notes for this order..."
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowOrderDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreateOrder}
              disabled={!selectedPatient || !selectedPanel || submitting}
              className="bg-teal-600 hover:bg-teal-700"
            >
              {submitting ? (
                <SpinnerGap size={18} className="animate-spin mr-2" />
              ) : (
                <Plus size={18} className="mr-2" />
              )}
              Create Order
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload Results Dialog */}
      <Dialog open={showResultsDialog} onOpenChange={setShowResultsDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload Lab Results</DialogTitle>
            <DialogDescription>
              {selectedOrder?.patient_name} - {selectedOrder?.panel?.name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <Label className="mb-2 block">Results (JSON format)</Label>
            <Textarea
              value={resultsJson}
              onChange={(e) => setResultsJson(e.target.value)}
              placeholder={`{
  "Glucose": {"value": 95, "unit": "mg/dL", "status": "normal"},
  "HbA1c": {"value": 5.4, "unit": "%", "status": "normal"},
  "TSH": {"value": 2.1, "unit": "mIU/L", "status": "normal"}
}`}
              rows={10}
              className="font-mono text-sm"
            />
            <p className="text-xs text-slate-500 mt-2">
              Paste the results from SiPhox or Rythm dashboard in JSON format
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowResultsDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleUploadResults}
              disabled={!resultsJson || submitting}
              className="bg-teal-600 hover:bg-teal-700"
            >
              {submitting ? (
                <SpinnerGap size={18} className="animate-spin mr-2" />
              ) : (
                <Upload size={18} className="mr-2" />
              )}
              Upload Results
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
